import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export const USER_CATEGORY_KEY = 'USER_CATEGORY';
export const USER_GUIDE_DONE_KEY = 'USER_GUIDE_DONE';

export type UserCategory = 'homebuyer' | 'realtor' | 'business' | 'researcher' | 'healthcare' | 'other' | 'unspecified';

import { SubscriptionPlans } from '../../user/user/user.model';
import { POPULATION } from '../../shared/types/feature-data-type';

// String literals used intentionally to avoid circular-dependency init-order issues
export const CATEGORY_TO_PLAN: Partial<Record<UserCategory, SubscriptionPlans>> = {
  homebuyer:  'HOME_SEARCH'    as SubscriptionPlans,
  realtor:    'REAL_ESTATE'    as SubscriptionPlans,
  business:   'SITE_SELECTION' as SubscriptionPlans,
  researcher: 'RESEARCH'       as SubscriptionPlans,
};

// Configurable — change to highlight a different layer in the guide. Population
// is the one metric every visitor already understands, and it sits in a group
// the data panel leaves open, so the highlight is on screen rather than folded
// away inside a collapsed section.
export const GUIDE_HIGHLIGHT_FEATURE: string = POPULATION;

// Configurable — minimum zoom level to complete the zoom task
export const GUIDE_MIN_ZOOM = 7;

// Configurable — promo code shown after completing all guide tasks
export const GUIDE_PROMO_CODE = 'BESTLOCATION';

export const PROMO_CODE_DISMISSED_KEY = 'PROMO_CODE_DISMISSED';

export type OnboardingTaskId =
  | 'goalSet'
  | 'layerExplored'
  | 'zoomedIn'
  | 'cellClicked';

/**
 * What the bar does when the visitor taps it. The bar shows one step at a
 * time, so the tap has exactly one meaning at any moment and never needs a
 * menu: 'card' opens the goal picker, the other three drive the surface the
 * step is about.
 */
export type OnboardingAction = 'card' | 'menu' | 'search' | 'hint';

export interface OnboardingStep {
  id: OnboardingTaskId;
  /** The one line of text the bar shows while this step is current. */
  label: string;
  /** The tap affordance, kept to two or three words so it never wraps. */
  cta: string;
  /**
   * Coach bubble under the bar, shown after a tap. Omitted where the tap
   * speaks for itself - highlighting the field beats narrating it.
   */
  hint?: string;
  /** Flashed in place of the label for a beat when the step is ticked off. */
  done: string;
  action: OnboardingAction;
}

/**
 * Order is the progression: the current step is the first one not yet done,
 * and the percentage is how many of these are behind it. Adding a step here
 * is the whole edit - the bar, the percentage and the completion state all
 * read from this list.
 *
 * Search leads because it is the one action that makes every later step mean
 * something - the rest of the map is empty until the visitor is somewhere they
 * care about. Opening a cell follows it, and a search ends by opening one on
 * the visitor's behalf, so the step often ticks itself the moment they land -
 * which is the reward for searching, not a step skipped. Tailoring comes last,
 * as the step that hands over the promo code.
 */
export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {
    id: 'zoomedIn',
    label: 'Search any US address for local demographics',
    cta: 'Search',
    done: 'Zoomed in. That is your patch',
    action: 'search',
  },
  {
    id: 'cellClicked',
    label: 'Tap any colored area for its numbers',
    cta: 'Where?',
    hint: 'Every colored cell on the map opens its own numbers.',
    done: 'There are the local numbers',
    action: 'hint',
  },
  {
    id: 'layerExplored',
    label: 'Click "Population" to color the map',
    cta: 'Show me',
    hint: 'Population is highlighted in the data panel and in any cell you open. Pick any layer to color the map.',
    // Replaced at run time by the layer the visitor actually chose.
    done: 'Map colored',
    action: 'menu',
  },
  {
    id: 'goalSet',
    label: 'Tailor the map to what you do',
    cta: 'Choose',
    hint: 'Pick what you do and the data panel presets itself to match.',
    done: 'Map tailored to your work',
    action: 'card',
  },
];

/** What hangs under the bar: the goal picker, the reward, or nothing. */
export type OnboardingCard = 'goal' | 'promo' | null;

@Injectable({ providedIn: 'root' })
export class UserGuideService {
  // Emits the featureConst key that should receive the purple highlight in the menu.
  // null = no highlight active.
  readonly highlightFeature$ = new BehaviorSubject<string | null>(null);

  /**
   * True while the guide is ringing the search field. The map page paints the
   * ring, because the field belongs to the Mapbox control rather than to any
   * component that could carry the class itself.
   */
  readonly highlightSearch$ = new BehaviorSubject<boolean>(false);

  // Pulses true briefly when the user asks the guide to point at the layer
  // without having explored it yet — triggers a stronger visual in the right panel.
  readonly highlightPulse$ = new BehaviorSubject<boolean>(false);

  /**
   * Whether the progress line is on screen. The map layout reserves its strip
   * off this, so it is the one flag that changes page geometry.
   */
  readonly barVisible$ = new BehaviorSubject<boolean>(false);

  /** Tasks ticked off so far. Replaced rather than mutated, for OnPush. */
  readonly completed$ = new BehaviorSubject<ReadonlySet<OnboardingTaskId>>(
    new Set()
  );

  readonly totalSteps = ONBOARDING_STEPS.length;

  /** The step the bar is showing, or null once everything is done. */
  get currentStep(): OnboardingStep | null {
    const done = this.completed$.value;
    return ONBOARDING_STEPS.find((step) => !done.has(step.id)) ?? null;
  }

  get doneCount(): number {
    return this.completed$.value.size;
  }

  get isComplete(): boolean {
    return this.doneCount >= this.totalSteps;
  }

  get percent(): number {
    return Math.round((this.doneCount / this.totalSteps) * 100);
  }

  isDone(id: OnboardingTaskId): boolean {
    return this.completed$.value.has(id);
  }

  /** No-op when the task is already ticked, so listeners can fire freely. */
  complete(id: OnboardingTaskId): boolean {
    if (this.completed$.value.has(id)) return false;
    const next = new Set(this.completed$.value);
    next.add(id);
    this.completed$.next(next);
    if (next.size >= this.totalSteps) {
      localStorage.setItem(USER_GUIDE_DONE_KEY, 'true');
    }
    return true;
  }

  /** Marks every task done without the completion animations — returning visitors. */
  completeAll(): void {
    this.completed$.next(new Set(ONBOARDING_STEPS.map((step) => step.id)));
  }

  hide(): void {
    this.barVisible$.next(false);
    this.highlightFeature$.next(null);
    this.highlightPulse$.next(false);
    this.highlightSearch$.next(false);
  }
}
